import Style from "@client/controls/HTML/Style/component";
import { BackgroundContainer } from "@client/controls/Style/Background/container";
import TextStyle from "@client/controls/Style/Text";
import withIsVisibleHOC, { type WithIsVisibleHOCProps } from "@client/hocs/withIsVisible";
import { addImportant } from "@client/lib/misc";
import { findInNodes } from "@client/lib/tree";
import { type CompositeSettings, type MenuItem } from "@client/types";
import { debug } from "@common/lib/log";
import React from "react";
import ctpl from "./css.ctpl";
import ItemComponent from "./Item/component";
import MegaItemComponent from "./MegaItem/component";

export const INVISIBLE_PROPS: MenuProps & WithIsVisibleHOCProps = {
  autoExpandCurrent: false,
  id: "",
  className: "",
  hideSubmenuOnOutsideClick: false,
  items: [],
  isClickable: false,
  isVisible: false,
  showTitles: false
};

export interface MenuProps {
  autoExpandCurrent: boolean;
  id: string;
  items: MenuItem[];
  className: string;
  isClickable: boolean;
  showTitles: boolean;

  itemRadius?: string;

  itemBackgroundPrefix?: string;
  itemText?: CompositeSettings.Text | null;
  itemStyle?: React.CSSProperties;
  style?: React.CSSProperties;

  activeItemStyle?: React.CSSProperties;
  itemHoverStyle?: React.CSSProperties;
  subMenuBackgroundPrefix?: string;
  subMenuStyle?: React.CSSProperties;
  subMenuItemStyle?: React.CSSProperties;
  subMenuItemHoverStyle?: React.CSSProperties;
  subItemText?: CompositeSettings.Text | null;
  subItemRadius?: string;

  hideSubmenuOnOutsideClick: boolean;
}

interface State {
  expandedItemId: string | null;
}

interface MenuItemStyleProps {
  id: string;
  hoverStyle?: React.CSSProperties;
  subMenuHoverStyle?: React.CSSProperties;
}
const MenuStyle = (props: MenuItemStyleProps) => {
  const data = React.useMemo(
    () => ({
      selector: `#${props.id}`,
      hoverStyle: props.hoverStyle ? addImportant(props.hoverStyle) : undefined,
      subMenuHoverStyle: props.subMenuHoverStyle ? addImportant(props.subMenuHoverStyle) : undefined
    }),
    [props.id, props.hoverStyle, props.subMenuHoverStyle]
  );

  return <Style ctpl={ctpl} data={data} />;
};

class MenuBase extends React.Component<MenuProps, State> {
  constructor(props: MenuProps) {
    super(props);

    this.state = {
      expandedItemId: this._getDefaultExpandedItemId(props)
    };
  }

  public componentDidUpdate(prevProps: MenuProps) {
    if (this.props.autoExpandCurrent !== prevProps.autoExpandCurrent) {
      const expandedItemId = this._getDefaultExpandedItemId(this.props);
      if (expandedItemId !== this.state.expandedItemId) {
        this.setState({ expandedItemId });
      }
    }
  }

  public render() {
    const props = this.props;

    return (
      <div id={props.id} className={props.className} style={props.style}>
        <MenuStyle id={props.id} hoverStyle={props.itemHoverStyle} subMenuHoverStyle={props.subMenuItemHoverStyle} />

        {props.itemBackgroundPrefix && (
          <BackgroundContainer
            containerSelector={`#${props.id} > nav > ul > li.dropdown > a`}
            prefix={props.itemBackgroundPrefix}
            radius={props.itemRadius}
          />
        )}
        {props.subMenuBackgroundPrefix && (
          <BackgroundContainer
            containerSelector={`#${props.id} ul.dropdown-menu`}
            prefix={props.subMenuBackgroundPrefix}
            radius={props.subItemRadius}
          />
        )}

        {props.itemText && <TextStyle selector={`#${props.id} > nav > ul > li > a`} value={props.itemText} />}
        {props.subItemText && (
          <TextStyle selector={`#${props.id} > nav > ul > li .dropdown-menu > li > a`} value={props.subItemText} />
        )}

        <nav>
          <ul>
            {props.items.map((item) => {
              const itemStyle = item.isCurrent
                ? { ...(props.itemStyle || {}), ...(props.activeItemStyle || {}) }
                : props.itemStyle;
              if (item.megaOptions) {
                return (
                  <MegaItemComponent
                    key={item.id}
                    item={item}
                    onExpand={this._onExpand}
                    onToggleClick={this._onToggleClick}
                    getIsExpanded={this._getIsExpanded}
                    isClickable={props.isClickable}
                    style={itemStyle}
                    showTitles={props.showTitles}
                    subMenuStyle={props.subMenuStyle}
                    subMenuItemStyle={props.subMenuItemStyle}
                    onClickOutside={this._onItemClickOutside}
                  />
                );
              }

              return (
                <ItemComponent
                  key={item.id}
                  item={item}
                  onExpand={this._onExpand}
                  onToggleClick={this._onToggleClick}
                  getIsExpanded={this._getIsExpanded}
                  isClickable={props.isClickable}
                  style={itemStyle}
                  showTitles={props.showTitles}
                  subMenuStyle={props.subMenuStyle}
                  subMenuItemStyle={props.subMenuItemStyle}
                  onClickOutside={this._onItemClickOutside}
                />
              );
            })}
          </ul>
        </nav>
      </div>
    );
  }

  private _getDefaultExpandedItemId = (props: MenuProps): string | null => {
    if (!props.autoExpandCurrent) return null;

    const item = findInNodes<MenuItem>(props.items, (item) => item.isCurrent);
    return item ? item.parentId : null;
  };

  private _onExpand = (id: string) => {
    this.setState({ expandedItemId: id });
  };

  private _onItemClickOutside = () => {
    this.props.hideSubmenuOnOutsideClick && this.setState({ expandedItemId: null });
  };

  private _onToggleClick = (id: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!this.props.isClickable) return;

      const item = findInNodes(this.props.items, (i) => i.id === id)!;

      const isExpanded = this._getIsExpanded(id);
      this.setState({ expandedItemId: isExpanded ? item.parentId : item.id });
    };
  };

  private _getIsExpanded = (id: string) => {
    let item = findInNodes<MenuItem>(this.props.items, (item) => item.id === this.state.expandedItemId);

    while (item) {
      if (item.id === id) {
        debug(`item (${item.name}) is expanded`);
        return true;
      }

      item = item.parentId ? findInNodes<MenuItem>(this.props.items, (it) => it.id === item!.parentId) : undefined;
    }

    return false;
  };
}

const Menu = withIsVisibleHOC(MenuBase);
export default Menu;
